import { __awaiter } from "tslib";
/**
 * Generic helper for fetching all pages from a cursor-based REST API endpoint.
 *
 * This is the REST API equivalent of listAllPaginated from @tumelo/common,
 * designed for APIs that use cursor-based pagination instead of page tokens.
 *
 * Automatically merges array fields and Map fields across pages.
 *
 * @template TRequest - The request type (must include cursor and pageSize)
 * @template TResponse - The response type from a single page
 *
 * @param fetchPage - Function that fetches a single page of results
 * @param cursorField - Name of the field in the response that contains the next page cursor
 * @param request - The base request parameters (cursor will be overridden during pagination)
 *
 * @returns Promise resolving to the merged response with all pages combined
 *
 * @example
 * ```ts
 * // Automatically merges arrays and Maps across all pages
 * const result = await listAllPaginatedRest(
 *   listFilteredProxyIntermediaryBallots,
 *   'nextPageToken',
 *   { pageSize: 100, hasChecker: false }
 * )
 * // result.proxyIntermediaryBallotNames contains all names from all pages
 * // result.ballotDataMap contains merged data from all pages
 * ```
 */
export function listAllPaginatedRest(fetchPage, cursorField, request) {
    return __awaiter(this, void 0, void 0, function* () {
        let cursor = request.cursor;
        let firstResponse = null;
        const accumulatedArrays = {};
        const accumulatedMaps = {};
        while (true) {
            const response = yield fetchPage(Object.assign(Object.assign({}, request), { cursor }));
            if (!firstResponse) {
                firstResponse = response;
                // Initialize accumulators based on first response
                for (const [key, value] of Object.entries(response)) {
                    if (key === cursorField)
                        continue;
                    if (Array.isArray(value)) {
                        accumulatedArrays[key] = [...value];
                    }
                    else if (value instanceof Map) {
                        accumulatedMaps[key] = new Map(value);
                    }
                }
            }
            else {
                // Merge subsequent responses
                for (const [key, value] of Object.entries(response)) {
                    if (key === cursorField)
                        continue;
                    if (Array.isArray(value)) {
                        accumulatedArrays[key].push(...value);
                    }
                    else if (value instanceof Map) {
                        const mapValue = value;
                        mapValue.forEach((v, k) => {
                            accumulatedMaps[key].set(k, v);
                        });
                    }
                }
            }
            const nextCursor = response[cursorField];
            if (!nextCursor) {
                break;
            }
            cursor = nextCursor;
        }
        // Build final result
        const result = Object.assign({}, firstResponse);
        for (const [key, value] of Object.entries(accumulatedArrays)) {
            result[key] = value;
        }
        for (const [key, value] of Object.entries(accumulatedMaps)) {
            result[key] = value;
        }
        result[cursorField] = ''; // Clear the cursor in final result
        return result;
    });
}
